// src/components/league/VisibilityChoice.tsx
//
// Both create surfaces already offered this — as a single unchecked checkbox,
// last in the form, after entry fee, reading "Public league (anyone can join)".
// Nobody noticed it: zero of eight real leagues are public, while five of them
// cannot fill and a third of all signups never found a league to join.
//
// Presented as two equal options with their consequences spelled out, it reads
// as a decision instead of a footnote. Private stays the default so nothing
// becomes listed by surprise.
import React from 'react';

interface VisibilityChoiceProps {
  isPublic: boolean;
  onChange: (isPublic: boolean) => void;
  idPrefix?: string;
}

const OPTIONS = [
  {
    value: false,
    label: 'Private',
    blurb: 'Invite only. You share a code with the people you want in.',
  },
  {
    value: true,
    label: 'Public',
    blurb: 'Listed in Open Leagues so anyone looking for a league can find yours and join.',
  },
] as const;

export default function VisibilityChoice({
  isPublic,
  onChange,
  idPrefix = 'visibility',
}: VisibilityChoiceProps) {
  return (
    <fieldset>
      <legend className="text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">
        Who can join
      </legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {OPTIONS.map((opt) => {
          const selected = isPublic === opt.value;
          const id = `${idPrefix}-${opt.label.toLowerCase()}`;
          return (
            <label
              key={opt.label}
              htmlFor={id}
              className={`cursor-pointer border-2 p-3 transition-colors ${
                selected
                  ? 'border-ink-900 bg-secondary-50'
                  : 'border-secondary-200 hover:border-secondary-400'
              }`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  id={id}
                  name={idPrefix}
                  checked={selected}
                  onChange={() => onChange(opt.value)}
                  className="h-4 w-4 border-secondary-300"
                />
                <span
                  className={`font-display uppercase tracking-wide text-base leading-none ${
                    selected ? 'text-ink-900' : 'text-secondary-600'
                  }`}
                >
                  {opt.label}
                </span>
              </span>
              <span className="block text-xs text-secondary-600 mt-1.5 leading-relaxed">
                {opt.blurb}
              </span>
            </label>
          );
        })}
      </div>
      <p className="text-xs text-secondary-500 mt-2">
        You can change this later in league settings.
      </p>
    </fieldset>
  );
}
